import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { withAuth } from '../../hoc/Auth';
import { Wrapper, Container, Column } from '../../hoc/Layout';
import Api from '../../Api';
import SecretChat, { ChatList } from '../../Components/SecretChat';

import classes from '../../PagesOfUser/SecretChats/styles.css';

/**
 * Секретные чаты со стороны сотрудника.
 *
 * Отличий от клиентской страницы два: видны разговоры всех клиентов, и в
 * списке первым идёт имя клиента — сотруднику важно, с кем говорит, а не
 * только о чём. Переписка та же: шифруется на сервере и живёт сутки.
 */
function AdminSecretChats({ token }) {
    const intl = useIntl();
    const [chats, setChats] = useState([]);
    const [ttlHours, setTtlHours] = useState(24);
    const [activeId, setActiveId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState('');
    const [selected, setSelected] = useState(null);
    const [subject, setSubject] = useState('');
    const [firstMessage, setFirstMessage] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const loadChats = useCallback(
        async function () {
            try {
                const data = await new Api({ token }).listAdminSecretChats();
                if (!mountedRef.current) return;
                setChats((data && data.items) || []);
                if (data && data.ttlHours) setTtlHours(data.ttlHours);
                setError(null);
            } catch (e) {
                if (!mountedRef.current) return;
                setError(
                    intl.formatMessage({
                        id: 'SecretChats.error.list',
                        defaultMessage: 'Не удалось загрузить список чатов',
                    })
                );
            } finally {
                if (mountedRef.current) setIsLoading(false);
            }
        },
        [token, intl]
    );

    useEffect(
        function () {
            loadChats();
            const timer = setInterval(loadChats, 15000);
            return () => clearInterval(timer);
        },
        [loadChats]
    );

    useEffect(
        function () {
            if (!isFormOpen) return undefined;

            let cancelled = false;

            // Поиск клиента ждёт паузы в наборе: без задержки запрос уходил бы
            // на каждую букву.
            const timer = setTimeout(function () {
                new Api({ token })
                    .listAdminSecretChatAccounts(query.trim())
                    .then(function (data) {
                        if (cancelled || !mountedRef.current) return;
                        setAccounts((data && data.items) || []);
                    })
                    .catch(() => {});
            }, 300);

            return function () {
                cancelled = true;
                clearTimeout(timer);
            };
        },
        [token, query, isFormOpen]
    );

    const loadMessages = useCallback((id) => new Api({ token }).getAdminSecretChatMessages(id), [token]);
    const sendMessage = useCallback(
        (id, body, files) =>
            new Api({ token }).postAdminSecretChatMessage(id, body, files).then((data) => {
                loadChats();
                return data;
            }),
        [token, loadChats]
    );
    // Файл скачивается запросом с токеном: у простой ссылки нет заголовка
    // авторизации, и сервер ответил бы отказом.
    const downloadFile = useCallback(
        (chatId, fileId) => new Api({ token }).downloadSecretChatFile(chatId, fileId, true),
        [token]
    );

    const markRead = useCallback(
        (id) => {
            new Api({ token })
                .readAdminSecretChat(id)
                .then(loadChats)
                .catch(() => {});
        },
        [token, loadChats]
    );

    async function onCreate(e) {
        e.preventDefault();

        const topic = subject.trim();
        if (!topic || !accountId || isCreating) return;

        setIsCreating(true);
        try {
            const created = await new Api({ token }).createAdminSecretChat(
                Number(accountId),
                topic,
                firstMessage.trim() || undefined
            );
            if (!mountedRef.current) return;
            setSubject('');
            setFirstMessage('');
            setAccountId('');
            setSelected(null);
            setQuery('');
            setError(null);
            setIsFormOpen(false);
            await loadChats();
            if (created && created.id) setActiveId(created.id);
        } catch (err) {
            if (!mountedRef.current) return;
            setError(intl.formatMessage({ id: 'SecretChats.error.create', defaultMessage: 'Не удалось создать чат' }));
        } finally {
            if (mountedRef.current) setIsCreating(false);
        }
    }

    const active = chats.find((c) => c.id === activeId) || null;
    // У части аккаунтов имя и фамилия не заполнены — тогда в списке
    // показывается логин, иначе строка выглядела бы пустой.
    const clientName = (chat) => (chat.client ? chat.client.name || chat.client.username : '');

    return (
        <Wrapper>
            <Container>
                <Column width="1180">
                    <div className={classes.Head}>
                        <h1 className={classes.Title}>
                            <FormattedMessage id="SecretChats.admin.title" defaultMessage="Секретные чаты" />
                        </h1>
                        <span className={classes.Subtitle}>
                            <FormattedMessage
                                id="SecretChats.subtitle"
                                defaultMessage="Переписка шифруется и полностью удаляется через {hours} часа"
                                values={{ hours: ttlHours }}
                            />
                        </span>
                    </div>

                    {error ? <div className={classes.Error}>{error}</div> : null}

                    <div className={classes.Board}>
                        <ChatList
                            chats={chats}
                            activeId={activeId}
                            isLoading={isLoading}
                            onPick={setActiveId}
                            onNew={() => setIsFormOpen(true)}
                            titleOf={clientName}
                            subtitleOf={(chat) => chat.subject}
                        />
                        <SecretChat
                            chat={active}
                            side="admin"
                            ttlHours={ttlHours}
                            loadMessages={loadMessages}
                            sendMessage={sendMessage}
                            onRead={markRead}
                            downloadFile={downloadFile}
                            titleOf={(chat) => `${clientName(chat)} — ${chat.subject}`}
                        />
                    </div>

                    {isFormOpen ? (
                        <div
                            className={classes.Overlay}
                            onClick={(e) => {
                                if (e.target === e.currentTarget) setIsFormOpen(false);
                            }}
                        >
                            <form className={classes.Dialog} onSubmit={onCreate}>
                                <div className={classes.DialogTitle}>
                                    <FormattedMessage id="SecretChat.button.new" defaultMessage="Новый чат" />
                                </div>
                                {/* Клиент выбирается одним полем: раньше рядом стояли
                                    «Поиск клиента» и отдельный список, и по форме было
                                    непонятно, зачем их два. */}
                                <div className={classes.Picker}>
                                    <input
                                        className={classes.Field}
                                        value={selected ? (selected.name ? `${selected.name} (${selected.username})` : selected.username) : query}
                                        autoFocus
                                        placeholder={intl.formatMessage({
                                            id: 'SecretChats.field.client',
                                            defaultMessage: 'Клиент: начните вводить имя или логин',
                                        })}
                                        onChange={(e) => {
                                            setSelected(null);
                                            setAccountId('');
                                            setQuery(e.target.value);
                                        }}
                                    />
                                    {selected ? (
                                        <button
                                            type="button"
                                            className={classes.PickerClear}
                                            title={intl.formatMessage({
                                                id: 'SecretChats.button.clearClient',
                                                defaultMessage: 'Выбрать другого',
                                            })}
                                            onClick={() => {
                                                setSelected(null);
                                                setAccountId('');
                                                setQuery('');
                                            }}
                                        >
                                            ×
                                        </button>
                                    ) : null}
                                    {!selected && accounts.length > 0 ? (
                                        <div className={classes.Suggestions}>
                                            {accounts.map(function (account) {
                                                return (
                                                    <button
                                                        type="button"
                                                        key={account.id}
                                                        className={classes.Suggestion}
                                                        onClick={() => {
                                                            setSelected(account);
                                                            setAccountId(String(account.id));
                                                        }}
                                                    >
                                                        <span className={classes.SuggestionName}>
                                                            {account.name || account.username}
                                                        </span>
                                                        {account.name ? (
                                                            <span className={classes.SuggestionLogin}>
                                                                {account.username}
                                                            </span>
                                                        ) : null}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                                <input
                                    className={classes.Field}
                                    value={subject}
                                    maxLength={200}
                                    placeholder={intl.formatMessage({
                                        id: 'SecretChats.field.subject',
                                        defaultMessage: 'Тема разговора',
                                    })}
                                    onChange={(e) => setSubject(e.target.value)}
                                />
                                <textarea
                                    className={classes.Field}
                                    value={firstMessage}
                                    rows={3}
                                    maxLength={20000}
                                    placeholder={intl.formatMessage({
                                        id: 'SecretChats.field.first',
                                        defaultMessage: 'Первое сообщение (необязательно)',
                                    })}
                                    onChange={(e) => setFirstMessage(e.target.value)}
                                />
                                <div className={classes.DialogButtons}>
                                    <button
                                        type="button"
                                        className={classes.Cancel}
                                        onClick={() => setIsFormOpen(false)}
                                    >
                                        <FormattedMessage id="SecretChats.button.cancel" defaultMessage="Отмена" />
                                    </button>
                                    <button
                                        type="submit"
                                        className={classes.Create}
                                        disabled={isCreating || !subject.trim() || !accountId}
                                    >
                                        <FormattedMessage id="SecretChats.button.create" defaultMessage="Начать чат" />
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : null}
                </Column>
            </Container>
        </Wrapper>
    );
}

export default withAuth(AdminSecretChats);



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/SecretChats/index.js